import React from 'react';
import classNames from 'classnames';

import TextContentButtonGroup from './components/TextContentButtonGroup';

// variant = default | ImageAndContent | MultipleBlocks
const TextContent = ({ config, variant = 'default' }) => {
  const {
    elementAttribute,
    imageComponent,
    backgroundSelection: { backgroundColor },
    textContentSection: {
      textColor,
      textOrientation: textOrientationRaw,
      title: { text: titleText, tag: titleTag, size: titleSize },
      body: { text: bodyText, tag: bodyTag, size: bodySize },
    },
    buttonSection,
  } = config;
  const layoutSize = imageComponent?.size ?? 'Full Width';
  const TitleTag = titleTag ?? 'h2';
  const BodyTag = bodyTag ?? 'p';
  const textOrientation = textOrientationRaw
    .toLowerCase()
    .split(' ')
    .join('-');

  const {
    isMultipleBtn = false,
    button1,
    button2,
    button3,
    button4,
  } = buttonSection;

  return (
    <div
      className={classNames('w-full flex justify-center', {
        'px-5 md:px-[3.333vw] min-[1500px]:px-[50px]':
          variant === 'default' && layoutSize === 'Full Page',
        'items-start': textOrientation.startsWith('top'),
        'items-center':
          textOrientation.startsWith('center') ||
          textOrientation.startsWith('right') ||
          textOrientation.startsWith('left'),
        'items-end': textOrientation.startsWith('bottom'),
      })}
    >
      <div
        id={elementAttribute?.id ?? ''}
        className={classNames('text-gray md:mx-auto md:mb-0 md:text-center', {
          'mt-[30px] px-5 md:px-[3.333vw] min-[1500px]:px-[50px] py-5 md:py-[3.333vw] min-[1500px]:py-[50px]':
            variant === 'default',
          'px-0 py-0': variant !== 'default',
          'w-full':
            layoutSize === 'Full Width' || variant === 'ImageAndContent',
          'max-w-[1400px]': layoutSize === 'Full Page' && variant !== 'default',
          'w-full sm:w-[1400px]':
            layoutSize === 'Full Page' && variant === 'default',
          '!flex-row': textOrientation !== 'right',
          'content-start': textOrientation === 'left',
          'flex-row-reverse': textOrientation === 'right',
          'items-start': textOrientation === 'top-left',
          'items-start flex-row-reverse': textOrientation === 'top-right',
          'items-start justify-center': textOrientation === 'top-center',
          'items-end': textOrientation === 'bottom-left',
          'items-end flex-row-reverse': textOrientation === 'bottom-right',
          'items-end justify-center': textOrientation === 'bottom-center',
          'justify-center': textOrientation === 'center',
          'pt-0': variant === 'MultipleBlocks',
        })}
        style={{
          backgroundColor: backgroundColor ?? '',
        }}
      >
        <TitleTag
          className={classNames('text-3xl font-bold mb-5 md:mb-[30px]', {
            'text-3xl font-bold': titleSize === 'text-3xl',
            'text-[clamp(1.75rem,5.2vw,2.5rem)] leading-[clamp(2.25rem,6.25vw,3rem)] tracking-[.0125rem] font-bold':
              titleSize === 'text-4xl',
            'text-xs': titleSize === 'text-xs',
            'text-sm': titleSize === 'text-sm',
            'text-base': titleSize === 'text-base',
            'text-lg': titleSize === 'text-lg',
            'text-xl': titleSize === 'text-xl',
            'text-2xl': titleSize === 'text-2xl',
            'text-center': textOrientation.endsWith('center'),
            'text-left': textOrientation.endsWith('left'),
            'text-right': textOrientation.endsWith('right'),
          })}
          style={{
            color: textColor ?? '',
          }}
        >
          {titleText}
        </TitleTag>
        <BodyTag
          className={classNames('text-base [&_a]:font-bold', {
            'text-xs': bodySize === 'text-xs',
            'text-sm': bodySize === 'text-sm',
            'text-base': bodySize === 'text-base',
            'text-lg': bodySize === 'text-lg',
            'text-xl': bodySize === 'text-xl',
            'text-2xl': bodySize === 'text-2xl',
            'text-3xl': bodySize === 'text-3xl',
            'text-center justify-items-center': textOrientation.endsWith(
              'center'
            ),
            'text-left justify-items-start': textOrientation.endsWith('left'),
            'text-right justify-items-end': textOrientation.endsWith('right'),
          })}
          style={{
            color: textColor ?? '',
          }}
        >
          {!!bodyText && (
            <div
              className="[&_ul]:list-disc [&_ol]:list-[auto] [&_ul]:list-inside [&_ol]:list-inside [&_ul]:pl-4 [&_ol]:pl-4"
              dangerouslySetInnerHTML={{ __html: bodyText }}
            />
          )}
        </BodyTag>

        {!!button1 &&
          (button1?.ctaLink?.content || button1?.ctaButton?.content) && (
            <div
              className={classNames('flex flex-row items-center h-14', {
                'justify-center': textOrientation.endsWith('center'),
                'justify-start': textOrientation.endsWith('left'),
                'justify-end': textOrientation.endsWith('right'),
                'mt-8': variant !== 'MultipleBlocks',
                'mt-0': variant === 'MultipleBlocks',
              })}
            >
              <TextContentButtonGroup
                mainButton={button1}
                subButton={button3}
              />
              {!!isMultipleBtn && !!button2 && (
                <TextContentButtonGroup
                  className="ml-4"
                  mainButton={button2}
                  subButton={button4}
                />
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default TextContent;
